import {
  Box,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import TableContainer from "../../../../Components/TableContainer/TableContainer";
import MobileCardView from "../../../../Components/MobileCardView/MobileCardView";
import ReceiptIcon from "@mui/icons-material/Receipt";
import NewDepositModel from "./NewDepositModel";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { DATABASE } from "../../../../Config/firebase";
import { FUNCTIONS } from "../../../../Config/firebase";
import { httpsCallable } from "firebase/functions";
import moment from "moment";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import useDebounce from "../../../../Components/useDebounce/useDebounce";
import FundsPageShell from "../../../../Components/FundsPeriodTotals/FundsPageShell";
import FundsPageToolbar from "../../../../Components/FundsPeriodTotals/FundsPageToolbar";
import useFundsPeriodTotals, {
  FUNDS_PERIOD_TYPES,
} from "../../../../hooks/useFundsPeriodTotals";
import {
  filterByFundsPeriod,
  filterByClientSegment,
  getDefaultFundsPeriodStart,
  getDefaultFundsPeriodEnd,
  hasActiveFundsFilters,
  isCapitalRoutingRow,
} from "../../../../Utils/fundsPeriodTotals";
import CapitalRoutingChip from "../../../../Components/CapitalRoutingChip/CapitalRoutingChip";

const FundsDeposit = () => {
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [fundsDepositStatus, setFundsDepositStatus] = useState(null);
  const [dipositeList, setDipositeList] = useState([]);
  const [isDepositModalOpen, setIsDepositModalOpen] = useState(false);
  const [searchDeposit, setSearchDeposit] = useState("");
  const [usersMap, setUsersMap] = useState({});
  const [clientSegment, setClientSegment] = useState(null);
  const [processingDepositId, setProcessingDepositId] = useState(null);
  const debouncedSearch = useDebounce(searchDeposit, 500);
  const [periodStartDate, setPeriodStartDate] = useState(getDefaultFundsPeriodStart);
  const [periodEndDate, setPeriodEndDate] = useState(getDefaultFundsPeriodEnd);
  const [dateFilterActive, setDateFilterActive] = useState(false);
  const [datePickerResetKey, setDatePickerResetKey] = useState(0);
  const { totals: periodTotals, loading: periodTotalsLoading } = useFundsPeriodTotals({
    type: FUNDS_PERIOD_TYPES.DEPOSIT,
    startDate: periodStartDate,
    endDate: periodEndDate,
    usersMap,
  });

  const filtersActive = hasActiveFundsFilters({
    search: searchDeposit,
    status: fundsDepositStatus,
    clientSegment,
    dateFilterActive,
    periodStartDate,
    periodEndDate,
  });

  const resetAllFilters = () => {
    setSearchDeposit("");
    setFundsDepositStatus(null);
    setClientSegment(null);
    setDateFilterActive(false);
    setPeriodStartDate(getDefaultFundsPeriodStart());
    setPeriodEndDate(getDefaultFundsPeriodEnd());
    setDatePickerResetKey((key) => key + 1);
    setPage(0);
  };

  const handleClientSegmentChange = (segment) => {
    setClientSegment(segment);
    setPage(0);
  };

  const handlePeriodStartChange = (value) => {
    if (!value) return;
    setDateFilterActive(true);
    setPeriodStartDate(value);
    setPage(0);
    if (periodEndDate && value.isAfter(periodEndDate)) {
      setPeriodEndDate(value);
    }
  };

  const handlePeriodEndChange = (value) => {
    if (!value) return;
    setDateFilterActive(true);
    setPeriodEndDate(value);
    setPage(0);
    if (periodStartDate && value.isBefore(periodStartDate)) {
      setPeriodStartDate(value);
    }
  };

  // Detect mobile device (breakpoint at 960px)
  const isMobile = useMediaQuery("(max-width: 960px)");
  const fundsDepositStatusList = [
    {
      id: null,
      name: "All",
    },
    {
      id: 0,
      name: "Pending",
    },
    {
      id: 1,
      name: "Waiting",
    },
    {
      id: 2,
      name: "Approved",
    },
    {
      id: 3,
      name: "Cancelled - By User",
    },
    {
      id: 4,
      name: "Rejected",
    },
  ];
  useEffect(() => {
    getDocs(collection(DATABASE, "users"))
      .then((snap) => {
        const map = {};
        snap.docs.forEach((d) => {
          map[d.id] = { doc_id: d.id, ...d.data() };
        });
        setUsersMap(map);
      })
      .catch((err) => console.error("FundsDeposit: failed to load users", err));
  }, []);

  const parseTimestamp = (ts) => {
    if (!ts) return null;
    if (ts?.toDate && typeof ts.toDate === "function") return moment(ts.toDate());
    const seconds = ts?.seconds;
    const nanoseconds = ts?.nanoseconds;
    if (seconds == null) return null;
    const milliseconds = seconds * 1000 + Math.floor((nanoseconds || 0) / 1000000);
    return moment(milliseconds);
  };

  const cellEllipsisSx = {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "100%",
    display: "block",
  };

  const renderCompactDateTime = (ts) => {
    const dateP = parseTimestamp(ts);
    if (!dateP) {
      return (
        <Typography variant="body2" sx={{ color: "text.secondary", fontSize: 14 }}>
          --
        </Typography>
      );
    }
    return (
      <Box sx={{ lineHeight: 1.3, textAlign: "center", minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontSize: 15, fontWeight: 700, color: "text.primary", lineHeight: 1.2 }}>
          {dateP.format("DD/MM/YY")}
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary", fontSize: 13, lineHeight: 1.2 }}>
          {dateP.format("HH:mm")}
        </Typography>
      </Box>
    );
  };

  /** Approved, rejected, or cancelled — only then show Fin. date. */
  const getFinishedTimestamp = (row) => {
    const status = row?.request_status;
    if (status !== 2 && status !== 3 && status !== 4) return null;
    return row?.conclusion_date || row?.request_approval_time || null;
  };

  const formatUsd = (value) => {
    if (value == null || Number.isNaN(Number(value))) return null;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(value));
  };

  const getDepositAmount = (row) => parseFloat(row?.total_amount) || 0;

  const resolveTransactionDocId = (transaction) => {
    const fieldDocId = transaction?.doc_id;
    if (fieldDocId != null && String(fieldDocId).trim() !== "") {
      return String(fieldDocId).trim();
    }
    return transaction?.id || "";
  };

  const isDepositLedger = (ledger) => {
    const type = ledger?.transection_type;
    if (type === 2) return false;
    return type === 0 || type === undefined || type === null;
  };

  const getPreviousBalance = (row) => {
    if (row?.ledger_previous_balance != null && row?.ledger_previous_balance !== undefined) {
      const fromLedger = parseFloat(row.ledger_previous_balance);
      if (!Number.isNaN(fromLedger)) return fromLedger;
    }
    const ob = parseFloat(row?.original_balance);
    if (!Number.isNaN(ob) && ob > 0) return ob;

    const status = row?.request_status;
    const nb = parseFloat(row?.net_balance);
    if (!Number.isNaN(nb) && (status === 0 || status === 1)) return nb;
    return null;
  };

  const getNewBalanceAfterFinalization = (row) => {
    const status = row?.request_status;
    if (status !== 2 && status !== 3 && status !== 4) return null;

    if (status === 2 && row?.ledger_current_balance != null && row?.ledger_current_balance !== undefined) {
      const fromLedger = parseFloat(row.ledger_current_balance);
      if (!Number.isNaN(fromLedger)) return fromLedger;
    }

    const prev = getPreviousBalance(row);
    if (prev == null) return null;
    const amt = getDepositAmount(row);
    if (status === 2) return prev + amt;
    if (status === 3 || status === 4) return prev;
    return null;
  };

  const pickDepositLedgerFromDocs = (ledgerDocs) => {
    const candidates = ledgerDocs
      .map((snap) => ({ ledgerDocId: snap.id, ...snap.data() }))
      .filter(isDepositLedger);
    if (candidates.length === 0) return null;
    const named = candidates.find((l) => l.ledgerDocId?.startsWith("deposit_"));
    return named || candidates[0];
  };

  const attachLedgerBalances = (transaction, ledger) => {
    if (!ledger) return transaction;
    return {
      ...transaction,
      ledger_previous_balance: ledger.previous_balance,
      ledger_current_balance: ledger.current_balance,
    };
  };

  const enrichDepositsWithLedgerBalances = async (transactions) => {
    const ledgerByTxnId = new Map();

    const needsLedgerLookup = transactions.filter((transaction) => {
      if (transaction.request_status !== 2) return false;
      const ob = parseFloat(transaction.original_balance);
      return Number.isNaN(ob) || ob <= 0;
    });

    await Promise.all(
      needsLedgerLookup.map(async (transaction) => {
        const txnId = resolveTransactionDocId(transaction);
        if (!txnId || ledgerByTxnId.has(txnId)) return;

        const ledgerDocIds = [
          `deposit_${txnId}`,
          transaction?.id && transaction.id !== txnId ? `deposit_${transaction.id}` : null,
        ].filter(Boolean);

        for (const ledgerDocId of ledgerDocIds) {
          try {
            const ledgerSnap = await getDoc(doc(DATABASE, "ledgers", ledgerDocId));
            if (ledgerSnap.exists()) {
              const ledger = ledgerSnap.data();
              if (isDepositLedger(ledger)) {
                ledgerByTxnId.set(txnId, ledger);
                return;
              }
            }
          } catch (err) {
            console.warn(`Failed to load ledger ${ledgerDocId}`, err);
          }
        }
      })
    );

    const stillMissing = needsLedgerLookup.filter(
      (transaction) => !ledgerByTxnId.has(resolveTransactionDocId(transaction))
    );

    const IN_QUERY_LIMIT = 30;
    for (let i = 0; i < stillMissing.length; i += IN_QUERY_LIMIT) {
      const chunk = stillMissing.slice(i, i + IN_QUERY_LIMIT);
      const txnIds = [
        ...new Set(chunk.map((transaction) => resolveTransactionDocId(transaction)).filter(Boolean)),
      ];
      if (txnIds.length === 0) continue;

      try {
        const ledgersSnap = await getDocs(
          query(collection(DATABASE, "ledgers"), where("doc_id", "in", txnIds))
        );
        const ledgersByDocId = new Map();
        ledgersSnap.docs.forEach((ledgerDoc) => {
          const data = ledgerDoc.data();
          const key = String(data?.doc_id || "").trim();
          if (!key || !isDepositLedger(data)) return;
          const existing = ledgersByDocId.get(key) || [];
          existing.push(ledgerDoc);
          ledgersByDocId.set(key, existing);
        });

        ledgersByDocId.forEach((docs, key) => {
          const picked = pickDepositLedgerFromDocs(docs);
          if (picked) ledgerByTxnId.set(key, picked);
        });
      } catch (err) {
        console.warn("Failed to query ledgers by doc_id", err);
      }
    }

    return transactions.map((transaction) => {
      const ob = parseFloat(transaction.original_balance);
      if (!Number.isNaN(ob) && ob > 0) return transaction;
      if (transaction.request_status !== 2) return transaction;

      const txnId = resolveTransactionDocId(transaction);
      const ledger = ledgerByTxnId.get(txnId);
      return attachLedgerBalances(transaction, ledger);
    });
  };

  const renderBalanceCell = (value, tooltipNote) => {
    const formatted = formatUsd(value);
    if (!formatted) {
      return (
        <Box sx={{ width: "100%", textAlign: "center" }}>
          <Typography variant="body2" sx={{ color: "text.secondary", fontSize: 14 }}>
            --
          </Typography>
        </Box>
      );
    }
    return (
      <Tooltip title={tooltipNote ? `${formatted} — ${tooltipNote}` : formatted}>
        <Box sx={{ width: "100%", textAlign: "center" }}>
          <Typography
            variant="body2"
            sx={{
              fontSize: 14,
              fontWeight: 500,
              color: "text.primary",
              whiteSpace: "nowrap",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {formatted}
          </Typography>
        </Box>
      </Tooltip>
    );
  };

  const columns = [
    {
      field: "requested_at",
      headerName: "Req.",
      width: 84,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => renderCompactDateTime(params?.row?.date),
    },
    {
      field: "finished_at",
      headerName: "Fin.",
      width: 84,
      hideOnMobile: true,
      hideOnTablet: true,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const ts = getFinishedTimestamp(params?.row);
        if (!ts) {
          return (
            <Typography variant="body2" sx={{ color: "text.secondary", fontSize: 14 }}>
              --
            </Typography>
          );
        }
        return renderCompactDateTime(ts);
      },
    },
    {
      field: "user_name",
      headerName: "Client",
      flex: 1,
      minWidth: 280,
      cellClassName: "leftAlign",
      renderCell: (params) => {
        const name = params?.row?.user_name || "--";
        const email = params?.row?.email || "";
        return (
          <Tooltip title={email ? `${name}\n${email}` : name}>
            <Box sx={{ lineHeight: 1.35, minWidth: 0, width: "100%", textAlign: "left", py: 0.25 }}>
              <Typography variant="body2" sx={{ ...cellEllipsisSx, fontWeight: 600, fontSize: 14, color: "text.primary" }}>
                {name}
              </Typography>
              {email ? (
                <Typography variant="caption" sx={{ ...cellEllipsisSx, fontSize: 13, color: "text.secondary", display: "block" }}>
                  {email}
                </Typography>
              ) : null}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "total_amount",
      headerName: "Amount",
      width: 150,
      disableColumnMenu: true,
      renderCell: (params) => {
        const label = formatUsd(getDepositAmount(params.row)) || "--";
        return (
          <Tooltip title={label}>
            <Box sx={{ lineHeight: 1.3, textAlign: "center", width: "100%" }}>
              <Typography
                variant="body2"
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: "text.primary",
                  whiteSpace: "nowrap",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {label}
              </Typography>
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "payment_receipt",
      headerName: "Proof",
      width: 56,
      filterable: false,
      sortable: false,
      hideOnMobile: true,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Tooltip title="Download receipt">
          <IconButton
            size="small"
            onClick={() => {
              const url =
                params?.row?.payment_receipt?.replace(
                  /fiddux-5a1f1\.appspot\.com/g,
                  "fidduxai-rowy.appspot.com"
                ) || params?.row?.payment_receipt;
              if (url) window.open(url, "_blank");
            }}
          >
            <ReceiptIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
    {
      field: "notes",
      headerName: "Notes",
      flex: 0.65,
      minWidth: 165,
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
      cellClassName: "leftAlign",
      renderCell: (params) => {
        const notes = params?.row?.notes || "";
        const display = notes || "--";
        return (
          <Tooltip title={display}>
            <Typography
              variant="body2"
              sx={{
                ...cellEllipsisSx,
                fontSize: 14,
                color: display === "--" ? "text.secondary" : "text.primary",
              }}
            >
              {display}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: "txn_id",
      headerName: "TXN",
      width: 124,
      hideOnMobile: true,
      disableColumnMenu: true,
      renderCell: (params) => {
        const txn = params?.row?.txn_id;
        const display = txn != null && txn !== "" ? String(txn) : "--";
        return (
          <Tooltip title={display}>
            <Typography
              variant="body2"
              sx={{
                fontSize: 13,
                fontWeight: 500,
                fontFamily: "monospace",
                color: "text.primary",
                whiteSpace: "nowrap",
              }}
            >
              {display}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: "request_status",
      headerName: "Status",
      width: 132,
      minWidth: 112,
      disableColumnMenu: true,
      renderCell: (params) => {
        const { request_status } = params?.row;
        const isProcessing = processingDepositId === params?.row?.doc_id;
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              gap: "4px",
              flexWrap: "wrap",
              position: "relative",
            }}
          >
            {request_status === 0 && (
              <Tooltip
                title={
                  <Box>
                    <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>⏳ Awaiting OTP Confirmation</Typography>
                    <Typography variant="caption" sx={{ display: "block", color: "rgba(255,255,255,0.75)" }}>
                      Client submitted this deposit. Waiting for them to confirm via OTP in the app before admin review.
                    </Typography>
                    <Typography variant="caption" sx={{ display: "block", mt: 0.75, color: "#ff9800" }}>
                      Next step: client confirms → status moves to "Pending Review"
                    </Typography>
                  </Box>
                }
                arrow
                placement="left"
                componentsProps={{ tooltip: { sx: { maxWidth: 280, p: 1.5, bgcolor: "#1a1a2e", border: "1px solid rgba(255,255,255,0.15)" } } }}
              >
                <IconButton
                  disabled={isProcessing}
                  sx={{ background: "#ff9800", width: "30px", height: "30px", borderRadius: "4px", "&:hover": { background: "#f57c00" } }}
                  onClick={() => handleAcceptRejectOnChange(params?.row, 1)}
                >
                  {isProcessing ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <HourglassEmptyIcon sx={{ color: "#fff" }} />}
                </IconButton>
              </Tooltip>
            )}
            {request_status === 1 && (
              <>
                <Tooltip
                  title={
                    <Box>
                      <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>✅ Approve Deposit</Typography>
                      <Typography variant="caption" sx={{ display: "block", color: "rgba(255,255,255,0.75)" }}>
                        Confirm receipt of funds and credit client's balance. This action is irreversible.
                      </Typography>
                      <Typography variant="caption" sx={{ display: "block", mt: 0.75, color: "#4caf50" }}>
                        Status → Approved · Balance credited · Email sent to client
                      </Typography>
                    </Box>
                  }
                  arrow
                  placement="left"
                  componentsProps={{ tooltip: { sx: { maxWidth: 280, p: 1.5, bgcolor: "#1a1a2e", border: "1px solid rgba(255,255,255,0.15)" } } }}
                >
                  <IconButton
                    disabled={isProcessing}
                    sx={{ background: "#4caf50", width: "30px", height: "30px", borderRadius: "4px", "&:hover": { background: "#388e3c" } }}
                    onClick={() => handleAcceptRejectOnChange(params?.row, 2)}
                  >
                    {isProcessing ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <CheckIcon sx={{ color: "#fff" }} />}
                  </IconButton>
                </Tooltip>
                <Tooltip
                  title={
                    <Box>
                      <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>❌ Reject Deposit</Typography>
                      <Typography variant="caption" sx={{ display: "block", color: "rgba(255,255,255,0.75)" }}>
                        Reject this deposit. Client balance will not be credited. Use if funds were not received or are invalid.
                      </Typography>
                      <Typography variant="caption" sx={{ display: "block", mt: 0.75, color: "#f44336" }}>
                        Status → Rejected · No balance change · Client notified
                      </Typography>
                    </Box>
                  }
                  arrow
                  placement="right"
                  componentsProps={{ tooltip: { sx: { maxWidth: 280, p: 1.5, bgcolor: "#1a1a2e", border: "1px solid rgba(255,255,255,0.15)" } } }}
                >
                  <IconButton
                    disabled={isProcessing}
                    sx={{ background: "red", width: "30px", height: "30px", borderRadius: "4px", "&:hover": { background: "darkred" } }}
                    onClick={() => handleAcceptRejectOnChange(params?.row, 4)}
                  >
                    {isProcessing ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <CloseIcon sx={{ color: "#fff" }} />}
                  </IconButton>
                </Tooltip>
              </>
            )}
            {request_status === 2 && (
              <Tooltip
                title={
                  <Box>
                    <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>✅ Deposit Approved</Typography>
                    <Typography variant="caption" sx={{ display: "block", color: "rgba(255,255,255,0.75)" }}>
                      Funds confirmed and client balance credited. No further action required.
                    </Typography>
                  </Box>
                }
                arrow placement="left"
                componentsProps={{ tooltip: { sx: { maxWidth: 260, p: 1.5, bgcolor: "#1a1a2e", border: "1px solid rgba(255,255,255,0.15)" } } }}
              >
                <Chip size="small" label="Approved" sx={{ color: "#fff", backgroundColor: "#4CAF50", cursor: "default" }} />
              </Tooltip>
            )}
            {request_status === 3 && (
              <Tooltip
                title={
                  <Box>
                    <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>🚫 Deposit Cancelled</Typography>
                    <Typography variant="caption" sx={{ display: "block", color: "rgba(255,255,255,0.75)" }}>
                      This deposit was cancelled. No balance was credited.
                    </Typography>
                  </Box>
                }
                arrow placement="left"
                componentsProps={{ tooltip: { sx: { maxWidth: 260, p: 1.5, bgcolor: "#1a1a2e", border: "1px solid rgba(255,255,255,0.15)" } } }}
              >
                <Chip size="small" label="Cancelled" sx={{ color: "#fff", backgroundColor: "#F44336", cursor: "default" }} />
              </Tooltip>
            )}
            {request_status === 4 && (
              <Tooltip
                title={
                  <Box>
                    <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>❌ Deposit Rejected</Typography>
                    <Typography variant="caption" sx={{ display: "block", color: "rgba(255,255,255,0.75)" }}>
                      Admin rejected this deposit. Funds were not received or are invalid.
                    </Typography>
                  </Box>
                }
                arrow placement="left"
                componentsProps={{ tooltip: { sx: { maxWidth: 260, p: 1.5, bgcolor: "#1a1a2e", border: "1px solid rgba(255,255,255,0.15)" } } }}
              >
                <Chip size="small" label="Rejected" sx={{ color: "#fff", backgroundColor: "#D32F2F", cursor: "default" }} />
              </Tooltip>
            )}
            {isCapitalRoutingRow(params?.row) && (
              <CapitalRoutingChip routingId={params?.row?.capital_routing_id} />
            )}
          </Box>
        );
      },
    },
    {
      field: "previous_balance",
      headerName: "Prev. Balance",
      width: 132,
      minWidth: 128,
      hideOnMobile: true,
      headerAlign: "center",
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) =>
        renderBalanceCell(
          getPreviousBalance(params.row),
          "Balance when deposit was requested"
        ),
    },
    {
      field: "new_balance",
      headerName: "New Balance",
      width: 132,
      minWidth: 128,
      hideOnMobile: true,
      headerAlign: "center",
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const value = getNewBalanceAfterFinalization(params.row);
        const status = params.row?.request_status;
        const note =
          status === 2
            ? "After approval (credited)"
            : status === 3 || status === 4
            ? "After cancel/reject (unchanged)"
            : "";
        return renderBalanceCell(value, note);
      },
    },
  ];
  useEffect(() => {
    const unsubscribe = getFundsDeposit(
      page,
      pageSize,
      fundsDepositStatus,
      debouncedSearch,
      dateFilterActive ? periodStartDate : null,
      dateFilterActive ? periodEndDate : null,
      clientSegment
    );
    return () => {
      unsubscribe();
    };
  }, [page, pageSize, fundsDepositStatus, debouncedSearch, periodStartDate, periodEndDate, dateFilterActive, clientSegment, usersMap]);

  const getFundsDeposit = (
    currentPage = 0,
    pageSize = 50,
    fundsDepositStatus = null,
    debouncedSearch = "",
    periodStart = periodStartDate,
    periodEnd = periodEndDate,
    segmentFilter = clientSegment
  ) => {
    setIsLoading(true);
    let q = query(
      collection(DATABASE, "transection_requests"),
      where("request_type", "==", 0),
      orderBy("date", "desc")
    );
    if (fundsDepositStatus !== null) {
      q = query(q, where("request_status", "==", fundsDepositStatus));
    }
    const unsubscribe = onSnapshot(
      q,
      async (querySnapshot) => {
        const transactions = querySnapshot?.docs?.map((doc, index) => ({
          rowid: index + 1,
          id: doc.id,
          ...doc.data(),
        }));
        const transactionsWithUserNames = transactions?.map((transaction) => {
          const matchedUser = usersMap[transaction?.user_id];
          return {
            ...transaction,
            user_name: matchedUser?.name || matchedUser?.full_name || "Unknown",
            email: matchedUser?.email || "--",
            net_balance: matchedUser?.net_balance ?? 0,
            user_type: matchedUser?.user_type,
          };
        });
        const transactionsWithBalances = await enrichDepositsWithLedgerBalances(
          transactionsWithUserNames
        );
        const periodFiltered = filterByFundsPeriod(
          transactionsWithBalances,
          periodStart,
          periodEnd,
          0
        );
        const segmentFiltered = filterByClientSegment(periodFiltered, segmentFilter, usersMap);
        const filteredTransactions = debouncedSearch
          ? segmentFiltered?.filter(
              (transaction) =>
                transaction?.email
                  .toLowerCase()
                  .includes(debouncedSearch.toLowerCase()) ||
                transaction?.user_name
                  .toLowerCase()
                  .includes(debouncedSearch.toLowerCase())
            )
          : segmentFiltered;
        const paginatedTransactions = filteredTransactions?.slice(
          currentPage * pageSize,
          (currentPage + 1) * pageSize
        );
        setDipositeList(paginatedTransactions);
        setTotalCount(filteredTransactions?.length);
        setIsLoading(false);
      },
      (error) => {
        console.error("Error fetching deposits:", error);
        setIsLoading(false);
      }
    );
    return unsubscribe;
  };
  const getTimestamp = () => {
    const currentDate = moment.utc();
    const hour = currentDate.hour();
  
    if (hour >= 21) {
      currentDate.add(1, "day");
    }
  
    return currentDate.toDate();
  };
  
  

  const handleAcceptRejectOnChange = async (Data, Status) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `You want to ${
        Status === 2 ? "approve" : Status === 1 ? "process" : "reject"
      } this deposit`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#46adf2",
      cancelButtonColor: "#5b5d5d",
      confirmButtonText: "Yes",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      const rowKey = Data?.doc_id || Data?.id;
      setProcessingDepositId(rowKey);
      try {
        if (!Data?.user_id) {
          throw new Error("User ID is missing");
        }

        if (Status === 2) {
          const approveDeposit = httpsCallable(FUNCTIONS, "approveDeposit");
          const response = await approveDeposit({ txnId: Data?.doc_id });
          const payload = response?.data || {};

          if (payload?.status === "already_processed") {
            toast.info("Already processed. Data refreshed.", {
              autoClose: 3000,
            });
          } else {
            toast.success("Deposit status updated successfully!", {
              autoClose: 3000,
            });
          }
        } else {
          await updateDoc(doc(DATABASE, "transection_requests", Data?.doc_id), {
            request_status: Status,
            request_approval_time: Timestamp.now(),
          });
          toast.success("Deposit status updated successfully!", {
            autoClose: 3000,
          });
        }

        setPage(0);
        getFundsDeposit(page, pageSize, fundsDepositStatus);
      } catch (error) {
        console.error("❌ [ERROR] Error updating deposit status:", error);
        console.error("Error stack:", error.stack);
        toast.error(
          error.message || "Something went wrong. Try again!",
          "error"
        );
      } finally {
        setProcessingDepositId(null);
      }
    }
  };
  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(0);
    getFundsDeposit(0, newPageSize, fundsDepositStatus);
  };
  const handlePageChange = (newPage) => {
    setPage(newPage);
    getFundsDeposit(newPage, pageSize, fundsDepositStatus);
  };
  const handleFundDepositeStatus = (id) => {
    setFundsDepositStatus(id === null ? null : id);
    setPage(0);
  };
  return (
    <>
      <Box
        sx={{
          width: "100%",
          bgcolor: "background.default", // Usa o background do tema (dark mode)
          paddingTop:{xs:'60px', sm:"0px"}
        }}
      >
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FundsPageShell
              title="Funds Deposit"
              totalsProps={{
                totals: periodTotals,
                loading: periodTotalsLoading,
                type: "deposit",
                startDate: periodStartDate,
                endDate: periodEndDate,
              }}
              toolbar={
                <FundsPageToolbar
                  searchValue={searchDeposit}
                  onSearchChange={(event) => setSearchDeposit(event.target.value)}
                  onSearchClear={() => setSearchDeposit("")}
                  periodStartDate={periodStartDate}
                  periodEndDate={periodEndDate}
                  onPeriodStartChange={handlePeriodStartChange}
                  onPeriodEndChange={handlePeriodEndChange}
                  resetKey={datePickerResetKey}
                  filterActionsProps={{
                    statusList: fundsDepositStatusList,
                    statusValue: fundsDepositStatus,
                    onStatusChange: (e) =>
                      handleFundDepositeStatus(e.target.value === "" ? null : e.target.value),
                    statusPlaceholder: "Select Deposit",
                    clientSegment,
                    onClientSegmentChange: handleClientSegmentChange,
                    onResetFilters: resetAllFilters,
                    filtersActive,
                  }}
                />
              }
            />
          </Grid>
        </Grid>
        <Box
          sx={{
            width: "100%",
            paddingTop: "24px",
          }}
        >
          {/* Desktop: TableContainer (comportamento atual) */}
          {!isMobile && (
            <TableContainer
              rows={dipositeList}
              columns={columns}
              loading={isLoading}
              page={page}
              pageSize={pageSize}
              totalCount={totalCount}
              handlePageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              paginationShow={true}
              density="standard"
              rowHeight={58}
              hideFillerColumn
              sx={{
                "& .MuiDataGrid-columnHeaderTitle": { fontSize: 15, fontWeight: 700 },
                "& .MuiDataGrid-cell": {
                  fontSize: 14,
                  py: 1,
                  color: "text.primary",
                },
                "& .MuiDataGrid-row": {
                  "&:hover": { bgcolor: "rgba(42, 245, 152, 0.06)" },
                },
                "& .MuiDataGrid-row:nth-of-type(even)": {
                  bgcolor: "rgba(255, 255, 255, 0.02)",
                },
              }}
            />
          )}
          
          {/* Mobile: MobileCardView (nova visualização) */}
          {isMobile && (
            <MobileCardView
              rows={dipositeList}
              loading={isLoading}
              type="deposit"
              onProcess={(row) => handleAcceptRejectOnChange(row, 1)}
              onApprove={(row) => handleAcceptRejectOnChange(row, 2)}
              onReject={(row) => handleAcceptRejectOnChange(row, 4)}
              processingId={processingDepositId}
            />
          )}
        </Box>
      </Box>
      <NewDepositModel
        open={isDepositModalOpen}
        onClose={() => setIsDepositModalOpen(false)}
      />
    </>
  );
};

export default FundsDeposit;